import { useEffect, useState } from 'react';
import type { Health } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

/**
 * The other half of §3d: a poll of `GET /api/health` comparing `buildId`,
 * showing a NON-BLOCKING "a new version is available" prompt.
 *
 * Non-blocking is deliberate. Forcing a reload would discard whatever the user
 * was typing, and the deploy that triggered it is usually of no consequence to
 * the screen they are on. The dynamic-import guard handles the case where it
 * IS consequential.
 */
const POLL_INTERVAL_MS = 3 * 60 * 1000;

export function NewVersionBanner(): React.ReactElement | null {
  const [newVersion, setNewVersion] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        // Anonymous and shallow, so this poll costs nothing and keeps working
        // even when the session has lapsed.
        const health = await api<Health>('/api/health');
        if (!cancelled && health.buildId !== __BUILD_ID__) setNewVersion(true);
      } catch {
        // A failed poll is not worth surfacing; the next one will do.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  if (!newVersion) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-4 border-b bg-accent px-4 py-2 text-sm"
    >
      <span>A new version of the funnel is available.</span>
      <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}
